import { type HTMLMotionProps, motion } from "framer-motion";
type PageTransitionProps = HTMLMotionProps<"div"> & {
  direction?: 1 | -1; // +1 = forward, -1 = backward
  isInitial?: boolean;
  childKey: string;
  offset?: number;
};

// Gentle ease-out timing for subtle motion
const SLIDE_DURATION = 0.25;
const IOS_EASING = "easeOut";
export default function PageTransition({
  children,
  direction = 1,
  // default to forward if none provided
  isInitial = false,
  childKey,
  offset = 48
}: PageTransitionProps) {
  const transition = {
    duration: 0.2,
    ease: "easeInOut"
  };
  const variants = {
    enter: {
      x: isInitial ? 0 : direction > 0 ? offset : -offset,
      opacity: isInitial ? 1 : 0,
      transition: {
        opacity: {
          duration: 0.28,
          ease: IOS_EASING
        },
        x: {
          duration: 0.24,
          ease: IOS_EASING
        }
      }
    },
    // The page is in the center (fully visible)
    center: {
      x: 0,
      opacity: 1,
      transition: {
        duration: SLIDE_DURATION,
        ease: IOS_EASING
      }
    },
    // The page is "offscreen" (exiting to the left if forward, or to the right if backward)
    exit: {
      x: direction > 0 ? -offset : offset,
      opacity: 0,
      transition: {
        // Fade out fast, slide slightly slower
        opacity: {
          duration: 0.12,
          ease: "linear"
        },
        x: {
          duration: 0.18,
          ease: "easeIn"
        }
      }
    }
  };
  return <motion.div initial="enter" animate="center" exit="exit" variants={variants} transition={transition}
  // Normal flow, not absolute fill: height:100% fills definite-height
  // layouts (accounts-link, the desktop shell) exactly like the old
  // absolute version, and resolves to `auto` inside the page-scroll
  // layouts so content can grow the page (percentage heights against an
  // auto-height parent compute to auto). Only sound because every
  // AnimatePresence around this uses mode="wait" — pages never overlap;
  // sync mode would stack the exiting and entering pages vertically.
  style={{
    position: "relative",
    width: "100%",
    height: "100%"
  }} key={childKey} data-sentry-element="motion.div" data-sentry-component="PageTransition" data-sentry-source-file="PageTransition.tsx">
      {children}
    </motion.div>;
}